import React from "react";
import { Alert, Button, Form } from "react-bootstrap";
import {
  CampaignOverridesFlag,
  CouponOverridesFlag,
  OfferOverridesFlag,
} from "../../interfaces";

/**
 * Ties a bit in an entity's overrides flag to the update fields it covers, so
 * a save can tell when a field is being edited without being marked overridden.
 */
export interface IOverrideRequirement {
  bit: number;
  /** The bit's name, as the flags editor shows it. */
  label: string;
  /** Keys in the update payload this bit covers. */
  fields: string[];
}

export const DEAL_OVERRIDE_REQUIREMENTS: IOverrideRequirement[] = [
  {
    bit: CampaignOverridesFlag.ShippingCountries,
    label: "ShippingCountries",
    fields: ["countries"],
  },
  {
    bit: CampaignOverridesFlag.Domains,
    label: "Domains",
    fields: ["domains"],
  },
  {
    bit: CampaignOverridesFlag.TotalCommissionPercent,
    label: "TotalCommissionPercent",
    fields: ["totalCommission"],
  },
  {
    bit: CampaignOverridesFlag.Suspended,
    label: "Suspended",
    fields: ["suspended", "suspendedReason"],
  },
  {
    bit: CampaignOverridesFlag.ShopLink,
    label: "ShopLink",
    fields: ["shopLink"],
  },
];

export const COUPON_OVERRIDE_REQUIREMENTS: IOverrideRequirement[] = [
  {
    bit: CouponOverridesFlag.Countries,
    label: "Countries",
    fields: ["countries"],
  },
  {
    bit: CouponOverridesFlag.Dates,
    label: "Dates",
    fields: ["validFrom", "validTo"],
  },
  { bit: CouponOverridesFlag.Summary, label: "Summary", fields: ["summary"] },
  { bit: CouponOverridesFlag.Domains, label: "Domains", fields: ["domains"] },
  {
    bit: CouponOverridesFlag.Priority,
    label: "Priority",
    fields: ["priority"],
  },
];

export const OFFER_OVERRIDE_REQUIREMENTS: IOverrideRequirement[] = [
  {
    bit: OfferOverridesFlag.Countries,
    label: "Countries",
    fields: ["countries"],
  },
  {
    bit: OfferOverridesFlag.Dates,
    label: "Dates",
    fields: ["validFrom", "validTo"],
  },
  { bit: OfferOverridesFlag.Summary, label: "Summary", fields: ["summary"] },
  {
    bit: OfferOverridesFlag.ProhibitedContents,
    label: "ProhibitedContents",
    fields: ["hasProhibitedContents"],
  },
  { bit: OfferOverridesFlag.Domains, label: "Domains", fields: ["domains"] },
  { bit: OfferOverridesFlag.Priority, label: "Priority", fields: ["priority"] },
];

/**
 * Requirements whose fields are being changed while their bit is still off.
 * `flag` is the value about to be saved, so flags set during this edit count.
 */
export const missingOverrides = (
  changed: Record<string, unknown>,
  flag: number,
  requirements: IOverrideRequirement[]
): IOverrideRequirement[] =>
  requirements.filter(
    (r) =>
      r.fields.some((f) => changed[f] !== undefined) && (flag & r.bit) === 0
  );

export const withOverrides = (
  flag: number,
  missing: IOverrideRequirement[]
): number => missing.reduce((acc, r) => acc | r.bit, flag);

/**
 * Blocks the save until the missing flags are set, or the edit is explicitly
 * acknowledged as intentional.
 */
export const OverrideFlagWarning: React.FC<{
  missing: IOverrideRequirement[];
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
  onApply: () => void;
}> = ({ missing, acknowledged, onAcknowledge, onApply }) => {
  if (missing.length === 0) return null;

  return (
    <Alert variant="warning" className="mt-3 mb-0">
      <div className="fw-semibold">
        {missing.length === 1
          ? "An override flag is not set"
          : "Override flags are not set"}
      </div>
      <div className="small mt-1">
        These fields are being changed but are not marked as overridden. Set the
        matching flag so the change is recorded as a deliberate override.
      </div>
      <ul className="small mt-2 mb-2">
        {missing.map((r) => (
          <li key={r.bit}>
            {r.fields.join(", ")} → <strong>{r.label}</strong>
          </li>
        ))}
      </ul>
      <Button size="sm" variant="warning" onClick={onApply}>
        Set {missing.length === 1 ? "this flag" : "these flags"}
      </Button>
      <Form.Check
        type="checkbox"
        className="small mt-2 mb-0"
        label="Save anyway — this change is not an override"
        checked={acknowledged}
        onChange={(e) => onAcknowledge(e.target.checked)}
      />
    </Alert>
  );
};
